export interface Theme {
  id: string;
  name: string;
  // Primary dark color (hero, testimonials, footer)
  primary: string;
  primaryLight: string;
  primaryDark: string;
  // CTA button color
  cta: string;
  ctaHover: string;
  ctaText: string; // text color ON the CTA button
  // Surface color (warm bg for sections)
  surface: string;
  // Alternating section backgrounds
  sectionLight: string;
  sectionAlt: string;
  // Text colors
  heading: string;
  body: string;
  // Accent for small highlights
  accent: string;
  // Optional gradient for hero/dark sections
  gradient?: string;
}

const themes: Theme[] = [
  {
    id: 'ocean-teal-warm-sand',
    name: 'Ocean Teal – Warm Sand',
    primary: '#0F4C5C',
    primaryLight: '#1A6B7A',
    primaryDark: '#0A3540',
    cta: '#E36414',
    ctaHover: '#C8540F',
    ctaText: '#FFFFFF',
    surface: '#F0ECE6',
    sectionLight: '#F7F4F0',
    sectionAlt: '#EDE8E0',
    heading: '#0F2027',
    body: '#3B4D54',
    accent: '#E36414',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #1A6B7A 0%, #0F4C5C 50%, #0A3540 100%)',
  },
  {
    id: 'ocean-teal-peach-glow',
    name: 'Ocean Teal – Peach Glow',
    primary: '#0F4C5C',
    primaryLight: '#1A6B7A',
    primaryDark: '#0A3540',
    cta: '#E36414',
    ctaHover: '#C8540F',
    ctaText: '#FFFFFF',
    surface: '#F5EFE8',
    sectionLight: '#FBF6F1',
    sectionAlt: '#F2EAE0',
    heading: '#0F2027',
    body: '#3A4E56',
    accent: '#E36414',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #1A6B7A 0%, #0F4C5C 50%, #0A3540 100%)',
  },
  {
    id: 'ocean-teal-crisp',
    name: 'Ocean Teal – Crisp',
    primary: '#0F4C5C',
    primaryLight: '#1A6B7A',
    primaryDark: '#0A3540',
    cta: '#E36414',
    ctaHover: '#C8540F',
    ctaText: '#FFFFFF',
    surface: '#EEF2F3',
    sectionLight: '#F5F7F8',
    sectionAlt: '#EAF0F1',
    heading: '#0F2027',
    body: '#2D3E45',
    accent: '#E36414',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #1A6B7A 0%, #0F4C5C 50%, #0A3540 100%)',
  },
  {
    id: 'ocean-teal-golden-hour',
    name: 'Ocean Teal – Golden Hour',
    primary: '#0F4C5C',
    primaryLight: '#1A6B7A',
    primaryDark: '#0A3540',
    cta: '#E36414',
    ctaHover: '#C8540F',
    ctaText: '#FFFFFF',
    surface: '#F5EEE4',
    sectionLight: '#FAF7F2',
    sectionAlt: '#F0E9DE',
    heading: '#0F2027',
    body: '#364C55',
    accent: '#E36414',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #1A6B7A 0%, #0F4C5C 50%, #0A3540 100%)',
  },
  {
    id: 'rippling-plum',
    name: 'Rippling Plum',
    primary: '#470037',
    primaryLight: '#6B1856',
    primaryDark: '#2E0024',
    cta: '#F5A623',
    ctaHover: '#E0950A',
    ctaText: '#1A1A1A',
    surface: '#F0EBE3',
    sectionLight: '#F5F3F0',
    sectionAlt: '#EEE5DB',
    heading: '#1A1A1A',
    body: 'rgba(0, 0, 0, 0.7)',
    accent: '#F5A623',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #6B1856 0%, #470037 50%, #2E0024 100%)',
  },
  {
    id: 'midnight-gold',
    name: 'Midnight Gold',
    primary: '#1B1B2F',
    primaryLight: '#2E2E4A',
    primaryDark: '#0F0F1F',
    cta: '#E2B93B',
    ctaHover: '#CCA52E',
    ctaText: '#1B1B2F',
    surface: '#F5F3EE',
    sectionLight: '#F3F2EE',
    sectionAlt: '#E9E7E0',
    heading: '#1B1B2F',
    body: '#6E6E7A',
    accent: '#E2B93B',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #2E2E4A 0%, #1B1B2F 50%, #0F0F1F 100%)',
  },
  {
    id: 'forest-ember',
    name: 'Forest Ember',
    primary: '#1B3A2D',
    primaryLight: '#2A5A42',
    primaryDark: '#122820',
    cta: '#E85D3A',
    ctaHover: '#D04B2A',
    ctaText: '#FFFFFF',
    surface: '#F0EDE8',
    sectionLight: '#F0EDE8',
    sectionAlt: '#E4DFD7',
    heading: '#1B2A22',
    body: '#5F6B5E',
    accent: '#E85D3A',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #2A5A42 0%, #1B3A2D 50%, #122820 100%)',
  },
  {
    id: 'slate-coral',
    name: 'Slate Coral',
    primary: '#2D3142',
    primaryLight: '#434866',
    primaryDark: '#1E2130',
    cta: '#EF6461',
    ctaHover: '#E04D4A',
    ctaText: '#FFFFFF',
    surface: '#F2EFE9',
    sectionLight: '#F2EFE9',
    sectionAlt: '#E6E2DA',
    heading: '#2D3142',
    body: '#707585',
    accent: '#EF6461',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #434866 0%, #2D3142 50%, #1E2130 100%)',
  },
  {
    id: 'royal-sapphire',
    name: 'Royal Sapphire',
    primary: '#1A237E',
    primaryLight: '#283593',
    primaryDark: '#0D1254',
    cta: '#FF8F00',
    ctaHover: '#E67E00',
    ctaText: '#1A1A1A',
    surface: '#ECEFF1',
    sectionLight: '#ECEFF1',
    sectionAlt: '#DEE3E7',
    heading: '#1A1A2E',
    body: '#616573',
    accent: '#FF8F00',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #283593 0%, #1A237E 50%, #0D1254 100%)',
  },
  {
    id: 'wine-rose',
    name: 'Wine Rose',
    primary: '#4A0E2E',
    primaryLight: '#6B1844',
    primaryDark: '#320A1F',
    cta: '#F2A365',
    ctaHover: '#E8924F',
    ctaText: '#3A0E1E',
    surface: '#F5EDED',
    sectionLight: '#F5EDED',
    sectionAlt: '#EADEDE',
    heading: '#2A0E1E',
    body: '#7A6065',
    accent: '#F2A365',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #6B1844 0%, #4A0E2E 50%, #320A1F 100%)',
  },
  {
    id: 'charcoal-lime',
    name: 'Charcoal Lime',
    primary: '#212121',
    primaryLight: '#3A3A3A',
    primaryDark: '#141414',
    cta: '#C6FF00',
    ctaHover: '#A8D900',
    ctaText: '#1A1A1A',
    surface: '#F5F5F0',
    sectionLight: '#F5F5F0',
    sectionAlt: '#EAEAE3',
    heading: '#1A1A1A',
    body: '#666666',
    accent: '#C6FF00',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #3A3A3A 0%, #212121 50%, #141414 100%)',
  },
  {
    id: 'nordic-frost',
    name: 'Nordic Frost',
    primary: '#1E3A5F',
    primaryLight: '#2A5080',
    primaryDark: '#142840',
    cta: '#4ECDC4',
    ctaHover: '#3DBAB2',
    ctaText: '#1A2A3A',
    surface: '#EDF2F4',
    sectionLight: '#EDF2F4',
    sectionAlt: '#DFE6EA',
    heading: '#1E2D3D',
    body: '#5E6E7E',
    accent: '#4ECDC4',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #2A5080 0%, #1E3A5F 50%, #142840 100%)',
  },
  {
    id: 'terracotta-earth',
    name: 'Terracotta Earth',
    primary: '#5C3D2E',
    primaryLight: '#7A5240',
    primaryDark: '#3E2A1F',
    cta: '#E07B4F',
    ctaHover: '#C86A3F',
    ctaText: '#FFFFFF',
    surface: '#F4EDE4',
    sectionLight: '#F4EDE4',
    sectionAlt: '#E8DFD3',
    heading: '#3A2820',
    body: '#7A6B5E',
    accent: '#E07B4F',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #7A5240 0%, #5C3D2E 50%, #3E2A1F 100%)',
  },

  // ── Blue-based palettes ──────────────────────────────────────

  {
    id: 'sky-blue-amber',
    name: 'Sky Blue',
    primary: '#039BE5',
    primaryLight: '#29B6F6',
    primaryDark: '#0277BD',
    cta: '#FFB300',
    ctaHover: '#FFA000',
    ctaText: '#1A1A1A',
    surface: '#E8F4FD',
    sectionLight: '#EBF5FC',
    sectionAlt: '#D8EAF5',
    heading: '#0D2137',
    body: '#546E7A',
    accent: '#FFB300',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #29B6F6 0%, #039BE5 50%, #0277BD 100%)',
  },
  {
    id: 'electric-blue',
    name: 'Electric Blue',
    primary: '#039BE5',
    primaryLight: '#4FC3F7',
    primaryDark: '#01579B',
    cta: '#FF6D00',
    ctaHover: '#E65100',
    ctaText: '#FFFFFF',
    surface: '#EAF5FC',
    sectionLight: '#EAF5FC',
    sectionAlt: '#D8ECF5',
    heading: '#0A1929',
    body: '#5A7080',
    accent: '#FF6D00',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #4FC3F7 0%, #039BE5 50%, #01579B 100%)',
  },
  {
    id: 'deep-navy',
    name: 'Deep Navy',
    primary: '#0A1628',
    primaryLight: '#162A4A',
    primaryDark: '#060E1A',
    cta: '#2196F3',
    ctaHover: '#1976D2',
    ctaText: '#FFFFFF',
    surface: '#EDF1F5',
    sectionLight: '#EDF1F5',
    sectionAlt: '#DFE5EB',
    heading: '#0A1628',
    body: '#5C6B7A',
    accent: '#2196F3',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #162A4A 0%, #0A1628 50%, #060E1A 100%)',
  },
  {
    id: 'cobalt-coral',
    name: 'Cobalt Coral',
    primary: '#1A3A6B',
    primaryLight: '#2A5298',
    primaryDark: '#0F2440',
    cta: '#FF7043',
    ctaHover: '#F4511E',
    ctaText: '#FFFFFF',
    surface: '#EBF0F7',
    sectionLight: '#EBF0F7',
    sectionAlt: '#DCE3EE',
    heading: '#142438',
    body: '#607080',
    accent: '#FF7043',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #2A5298 0%, #1A3A6B 50%, #0F2440 100%)',
  },
  {
    id: 'steel-blue',
    name: 'Steel Blue',
    primary: '#37474F',
    primaryLight: '#546E7A',
    primaryDark: '#263238',
    cta: '#42A5F5',
    ctaHover: '#1E88E5',
    ctaText: '#FFFFFF',
    surface: '#ECEFF1',
    sectionLight: '#ECEFF1',
    sectionAlt: '#DEE3E7',
    heading: '#1C2A33',
    body: '#607D8B',
    accent: '#42A5F5',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #546E7A 0%, #37474F 50%, #263238 100%)',
  },
  {
    id: 'azure-gold',
    name: 'Azure Gold',
    primary: '#1565C0',
    primaryLight: '#1E88E5',
    primaryDark: '#0D47A1',
    cta: '#FDD835',
    ctaHover: '#F9C800',
    ctaText: '#1A1A1A',
    surface: '#E8EEF6',
    sectionLight: '#E8EEF6',
    sectionAlt: '#DAE2EE',
    heading: '#0D2240',
    body: '#5A6A7A',
    accent: '#FDD835',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #1E88E5 0%, #1565C0 50%, #0D47A1 100%)',
  },
  {
    id: 'indigo-mint',
    name: 'Indigo Mint',
    primary: '#283593',
    primaryLight: '#3949AB',
    primaryDark: '#1A237E',
    cta: '#26A69A',
    ctaHover: '#00897B',
    ctaText: '#FFFFFF',
    surface: '#E8EAF6',
    sectionLight: '#E8EAF6',
    sectionAlt: '#DADCEE',
    heading: '#1A1A3A',
    body: '#5C5F7E',
    accent: '#26A69A',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #3949AB 0%, #283593 50%, #1A237E 100%)',
  },
  {
    id: 'ocean-blue',
    name: 'Ocean Blue',
    primary: '#0D47A1',
    primaryLight: '#1565C0',
    primaryDark: '#08306B',
    cta: '#E91E63',
    ctaHover: '#C2185B',
    ctaText: '#FFFFFF',
    surface: '#E3F2FD',
    sectionLight: '#E8F4FD',
    sectionAlt: '#D4E8F5',
    heading: '#0A1A30',
    body: '#546A80',
    accent: '#E91E63',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #1565C0 0%, #0D47A1 50%, #08306B 100%)',
  },
  {
    id: 'arctic-teal',
    name: 'Arctic Teal',
    primary: '#004D6E',
    primaryLight: '#00728F',
    primaryDark: '#003450',
    cta: '#00BCD4',
    ctaHover: '#0097A7',
    ctaText: '#FFFFFF',
    surface: '#E0F4F7',
    sectionLight: '#E5F5F7',
    sectionAlt: '#D2EAEE',
    heading: '#0A2530',
    body: '#4D6F7A',
    accent: '#00BCD4',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #00728F 0%, #004D6E 50%, #003450 100%)',
  },
  {
    id: 'midnight-blue',
    name: 'Midnight Blue',
    primary: '#1A1A40',
    primaryLight: '#2C2C5E',
    primaryDark: '#10102A',
    cta: '#5C6BC0',
    ctaHover: '#3F51B5',
    ctaText: '#FFFFFF',
    surface: '#EEEEF5',
    sectionLight: '#EEEEF5',
    sectionAlt: '#E0E0EC',
    heading: '#1A1A35',
    body: '#6A6A82',
    accent: '#5C6BC0',
    gradient: 'radial-gradient(ellipse 80% 100% at 70% 50%, #2C2C5E 0%, #1A1A40 50%, #10102A 100%)',
  },
];

/** Theme applied for first-time visitors (no saved preference in localStorage). */
export const defaultTheme: Theme = themes.find((t) => t.id === 'ocean-teal-golden-hour') ?? themes[0];

export default themes;
